import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock'

import algoliasearch from 'algoliasearch/lite'
import instantsearch from 'instantsearch.js'
import { configure } from 'instantsearch.js/es/widgets'
import { connectSearchBox, connectHits } from 'instantsearch.js/es/connectors'

// NOTE: This is a public, search-only key. See https://www.algolia.com/account/api-keys/all
const searchClient = algoliasearch('22UVVTIMMW', '42e9eea37c39c3ba571ca0112231197f')

let firstLoad = true
let searchDebounce
const search = instantsearch({
  indexName: 'prod_uk_ITEMS',
  searchClient,
  future: {
    preserveSharedStateOnUnmount: true
  },
  searchFunction (helper) {
    if (firstLoad) {
      firstLoad = false
      return
    }
    clearTimeout(searchDebounce)
    searchDebounce = setTimeout(() => helper.search(), 300)
  }
})

const hasContent = obj => obj && Object.keys(obj).length > 0

const renderSearchBox = (renderOptions, isFirstRender) => {
  const { query, refine, clear } = renderOptions
  const container = document.querySelector('#searchbox')

  if (isFirstRender) {
    // Create the input field
    const searchInput = document.createElement('input')
    searchInput.setAttribute('id', 'searchbox-input')
    searchInput.classList.add('searchbox__input')
    searchInput.setAttribute('autocomplete', 'off')
    searchInput.setAttribute('autocorrect', 'off')
    searchInput.setAttribute('autocapitalize', 'off')
    searchInput.setAttribute('placeholder', 'Search by model, body type...')
    searchInput.setAttribute('spellcheck', 'false')
    searchInput.setAttribute('maxlength', '512')
    searchInput.setAttribute('type', 'search')

    // Create the reset button
    const resetButton = document.createElement('button')
    resetButton.classList.add('searchbox__reset')
    resetButton.setAttribute('type', 'reset')
    resetButton.setAttribute('title', 'Clear the search query')
    resetButton.setAttribute('data-interaction-section', 'Search results')
    resetButton.setAttribute('data-interaction-element', 'Reset')

    searchInput.addEventListener('input', event => {
      refine(event.target.value)
    })

    resetButton.addEventListener('click', () => {
      clear()
      clearSearchResults()
    })

    container.appendChild(searchInput)
    container.appendChild(resetButton)
  }

  container.querySelector('input').value = query
}

const transformItems = (items, results) => {
  if (!results || results.query === '') {
    return []
  }
  // If there is only one result, and it is a model, let's keep the alternatives.
  // If there is more than one, those extra results are *the alternatives* and so we want to remove them.
  // There are probably better ways to handle this but "nulling" the property does the job, for now.
  // If nothing else, it is a good test case for the transformItems approach.
  if (items.length === 1 && items[0].type === 'model') return items

  return items.map(item => ({
    ...item,
    alternatives: null
  }))
}

const groupItemsByType = (items) => {
  return items.reduce((groupedItemsObj, item) => {
    const group = item.type
    groupedItemsObj[group] = groupedItemsObj[group] || []
    groupedItemsObj[group].push(item)
    return groupedItemsObj
  }, {})
}

const renderHits = (renderOptions) => {
  const { items, results } = renderOptions
  const transformedItems = transformItems(items, results)
  const groupedItems = groupItemsByType(transformedItems)
  const resultsContainer = document.querySelector('#hits')

  // Collect alternatives to render in a separate section
  const alternatives =
    transformedItems
      .filter(item => item.type === 'model' && item.alternatives)
      .flatMap(item => item.alternatives)

  // Convert to array
  const groupedItemsByType = Object.entries(groupedItems).map(([type, hits]) => ({
    type,
    hits
  }))

  // Empty state
  if (results && results.query === '') {
    resultsContainer.innerHTML = renderEmptyState()
    return
  }

  // Render results
  resultsContainer.innerHTML = `
    ${renderSearchResults(transformedItems, groupedItemsByType, results)}
    ${renderAlternativesSection(alternatives)}
  `
}

const searchResultHTML = (hit) => {
  if (hit.type === 'plp page') {
    return renderPLPItem(hit)
  } else if (hit.type === 'generic page') {
    return renderGenericItem(hit)
  } else if (hit.type === 'model') {
    return renderModelItem(hit)
  }
}

const renderSearchResults = (resultsItems, groupedItems, results) => {
  if (resultsItems.length === 0 && (results?.query)) {
    return `
      <p class="search-results__no-results">No results for <q>${results.query}</q>. Try another search!</p>
      ${renderEmptyState()}
    `
  }

  return `
    ${groupedItems
      .map(
        (groupedItems) => `
          <section class="search-results__section">
            ${groupedItems.type === 'model' ? '<h2 class="search-results__section-title">Top models</h2>' : ''}
            <ul class="search-results__list">
              ${groupedItems.hits
                .map(
                  (hit) => `
                  <li class="search-results__item">
                    ${searchResultHTML(hit)}
                  </li>
                `
                )
                .join('')}
            </ul>
          </section>
        `
      )
      .join('')}
  `
}

const renderEmptyState = () => {
  const carDiscoveryUrl = document.querySelector('.search-modal__results').getAttribute('data-car-discovery-url')
  const trendingPrompts = ['BMW', 'Audi A1', 'SUVs', 'Ford Kuga']
  const popularMakesPrompts = ['Audi', 'Kia', 'BMW', 'Volvo']
  const carTypesPrompts = ['SUVs', 'Hatchbacks', 'Saloons', 'Estates']

  return `
    <div class="search-empty-state">
      <div class="search-empty-state__section search-empty-state__section--prompts">
        <div class="search-empty-state__sub-section">
          <h3 class="search-empty-state__title">Trending</h3>
          <ul class="search-empty-state__prompts-group list-unstyled">
            ${
              trendingPrompts.map(item => `
                <li>
                  <button class="btn btn-full-width search-prompt-btn" data-prompt-btn data-interaction-section="Search empty state" data-interaction-category="Trending" data-interaction-element="${item}">
                    <svg class="search-prompt-btn__icon" viewBox="0 0 24 24">
                      <use href="#sprite_icon/graph"></use>
                    </svg>
                    ${item}
                  </button>
                </li>
              `).join('')
            }
          </ul>
        </div>
        <div class="search-empty-state__sub-section">
          <h3 class="search-empty-state__title">Popular makes</h3>
          <ul class="search-empty-state__prompts-group list-unstyled">
            ${
              popularMakesPrompts.map(item => `
                <li>
                  <button class="btn btn-full-width search-prompt-btn" data-prompt-btn data-interaction-section="Search empty state" data-interaction-category="Popular makes" data-interaction-element="${item}">
                    <svg class="search-prompt-btn__icon" viewBox="0 0 24 24">
                      <use href="#sprite_icon/makes/${item.toLowerCase().split(' ').join('_')}"></use>
                    </svg>
                    ${item}
                  </button>
                </li>
              `).join('')
            }
          </ul>
        </div>
        <div class="search-empty-state__sub-section">
          <h3 class="search-empty-state__title">Car types</h3>
          <ul class="search-empty-state__prompts-group list-unstyled">
            ${
              carTypesPrompts.map(item => `
                <li>
                  <button class="btn btn-full-width search-prompt-btn" data-prompt-btn data-interaction-section="Search empty state" data-interaction-category="Car types" data-interaction-element="${item}">
                    ${item}
                  </button>
                </li>
              `).join('')
            }
          </ul>
        </div>
      </div>
      <div class="search-empty-state__section search-empty-state__section--quiz">
        <h3 class="search-empty-state__title">Not sure what you’re looking for?</h3>
        <a href="${carDiscoveryUrl}" class="btn btn--naked-light btn-full-width" data-interaction-section="Search empty state" data-interaction-element="Take the quiz">Take the quiz</a>
      </div>
    </div>
  `
}

const renderAlternativesSection = (alternatives) => {
  if (!alternatives || alternatives.length === 0) {
    return ''
  }

  return `
    <section class="search-results__section">
      <h3 class="search-results__section-title">Alternatives to consider</h3>
      <ul class="search-results__list">
        ${alternatives.map((alt) => `
          <li class="search-results__item">
            ${renderModelItem(alt, true)}
          </li>
        `).join('')}
      </ul>
    </section>
  `
}

const renderGenericItem = (hit) => {
  return `
    <div class="search-result">
      <a href="${hit.url}" class="search-result__cta" data-interaction-section="Search results" data-interaction-category="${hit.interaction.category}" data-interaction-element="${hit.interaction.element}">
        <span class="search-result__cta-title">${hit.name}</span>
        <span class="search-result__cta-price">${hit.subtitle}</span>
      </a>
    </div>
  `
}

const renderPLPItem = (hit) => {
  return `
    <div class="search-result">
      <h2 class="search-result__title">
        ${hit.name}
      </h2>
      <a href="${hit.url}" class="search-result__cta" data-interaction-section="Search results" data-interaction-category="${hit.interaction.category}" data-interaction-element="${hit.interaction.element}">
        <span class="search-result__cta-title">${hit.title}</span>
        <span class="search-result__cta-price">${hit.subtitle}</span>
      </a>
    </div>
  `
}

const renderModelItem = (hit, alternative = false) => {
  return `
    <div class="search-result">
      <h2 class="search-result__title">
        ${hit.nameForDisplay}
      </h2>
      <div class="search-result__wowscore wowscore-pill" data-score="${hit.wowscore}">${hit.wowscore}/10</div>
      <div class="search-result__body">
        <img src="${hit.image}" class="search-result__img" alt=""/>
        <div class="search-result__ctas-wrap">
        ${hasContent(hit.newCta)
          ? `
            ${hasContent(hit.newCta.badge)
              ? `
                <div class="search-result__cta-badge badge badge--small badge--blue">
                  ${hit.newCta.badge}
                </div>
              `
              : ''
            }
            <a href="${hit.newCta.url}" class="search-result__cta" data-interaction-section="Search results" data-interaction-category="${alternative ? 'Alternative ' : ''}${hit.newCta.interaction.category}" data-interaction-element="${hit.newCta.interaction.element}">
              <span class="search-result__cta-title">${hit.newCta.title}</span>
              ${hit.newCta.subtitle ? `<span class="search-result__cta-price">${hit.newCta.subtitle}</span>` : ''}
            </a>
          `
          : ''
        }
        ${hasContent(hit.stockCta)
          ? `
            <a href="${hit.stockCta.url}" class="search-result__cta" data-interaction-section="Search results" data-interaction-category="${alternative ? 'Alternative ' : ''}${hit.stockCta.interaction.category}" data-interaction-element="${hit.stockCta.interaction.element}">
              <span class="search-result__cta-title">${hit.stockCta.title}</span>
              <span class="search-result__cta-price">${hit.stockCta.subtitle}</span>
            </a>
          `
          : ''
        }
        </div>
      </div>
    </div>
  `
}

const clearSearchResults = () => {
  document.querySelector('#searchbox-input').value = ''
  document.querySelectorAll('.search-results__section').forEach((e) => e.remove())
}

const closeSearchModal = (searchModal, disableOpeningOfSearchModal, enableOpeningOfSearchModal) => {
  disableOpeningOfSearchModal()
  searchModal.close()
  clearAllBodyScrollLocks()
  clearSearchResults()

  const noResultsContainer = document.querySelector('.search-results__no-results')
  if (noResultsContainer) {
    noResultsContainer.remove()
  }
  enableOpeningOfSearchModal()
}

const openSearchModal = (searchModal, hitsContainer, searchModalScrollArea) => {
  searchModal.showModal()
  disableBodyScroll(searchModalScrollArea)
  hitsContainer.innerHTML = renderEmptyState()
}

const setUpSearchModal = () => {
  const searchModal = document.querySelector('#search-modal-dialog')
  const searchModalScrollArea = document.querySelector('#search-modal-results')
  const openButton = document.querySelector('#search-modal-open')
  const closeButton = document.querySelector('#search-modal-close')
  const hitsContainer = document.querySelector('#hits')

  openButton.addEventListener('click', () => {
    openSearchModal(searchModal, hitsContainer, searchModalScrollArea)
  })

  // When opening the modal via focus, the browser saves this info and will focus the
  // same element again when dismissing the modal, this crashes chrome and makes  it
  // impossible to close the modal on Safari. So explicitly disable, then enable the
  // modal opening whilst it is in the process of closing.
  let allowSearchModalOpening = true
  openButton.addEventListener('focus', () => {
    if (allowSearchModalOpening) {
      openSearchModal(searchModal, hitsContainer, searchModalScrollArea)
    }
  })

  const disableOpeningOfSearchModal = () => {
    allowSearchModalOpening = false
  }

  const enableOpeningOfSearchModal = () => {
    allowSearchModalOpening = true
  }

  closeButton.addEventListener('click', () => {
    closeSearchModal(searchModal, disableOpeningOfSearchModal, enableOpeningOfSearchModal)
  })

  searchModal.addEventListener('click', (e) => {
    if (e.target === searchModal) {
      closeSearchModal(searchModal)
    }
  })
}

const customSearchBox = connectSearchBox(renderSearchBox)
const customHits = connectHits(renderHits)

// if there is a searchbox element, i.e. we are in the variant, we can set things up!
if (document.querySelector('#searchbox')) {
  search.addWidgets([
    customSearchBox({
      container: document.querySelector('#searchbox')
    }),
    configure({
      hitsPerPage: 24
    }),
    customHits({
      container: '#hits'
    })
  ])

  search.start()
  setUpSearchModal()
  document.querySelector('#hits').innerHTML = renderEmptyState()

  document.querySelector('#hits').addEventListener('click', (e) => {
    const prompt = e.target.closest('[data-prompt-btn]')
    if (prompt) {
      const queryFromPrompt = prompt.innerText
      document.getElementById('searchbox-input').value = queryFromPrompt
      search.helper.setQuery(queryFromPrompt).search() // Manually trigger Algolia search
    }
  })

  search.on('error', ({ error }) => {
    window.Bugsnag.notify(new Error(`Algolia instantsearch error: ${error}`))
  })
}
